import { useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@artstorefronts/ui';
import { ChevronDown, ChevronRight, MapPin, Users, UserX } from 'lucide-react';

import { dbscan } from '../../utils/dbscan';
import type { ArtistCentralityData } from './CentralityMap';

interface CentralityDataPanelProps {
  data: ArtistCentralityData[];
  onArtistClick?: (artist: ArtistCentralityData) => void;
  selectedArtist?: ArtistCentralityData | null;
}

interface Cluster {
  label: string;
  members: ArtistCentralityData[];
}

function ArtistRow({
  artist,
  onClick,
  selected,
  showNeighborCount,
}: {
  artist: ArtistCentralityData;
  onClick?: () => void;
  selected?: boolean;
  showNeighborCount?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-zinc-100 ${
        selected ? 'bg-zinc-100' : ''
      }`}
    >
      {artist.profile.avatarUrl ? (
        <img src={artist.profile.avatarUrl} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] text-zinc-500">
          {(artist.profile.displayName ?? '?')[0]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-zinc-900">{artist.profile.displayName ?? 'Unknown'}</p>
        <p className="truncate text-zinc-500">
          {[artist.profile.city, artist.profile.region].filter(Boolean).join(', ') || 'No location'}
        </p>
      </div>
      {showNeighborCount && <span className="shrink-0 text-[10px] text-zinc-400">{artist.neighborCount}n</span>}
    </button>
  );
}

function ClusterCard({
  cluster,
  onArtistClick,
  selectedArtist,
}: {
  cluster: Cluster;
  onArtistClick?: (a: ArtistCentralityData) => void;
  selectedArtist?: ArtistCentralityData | null;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs">
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
        )}
        <span className="flex-1 font-medium text-zinc-900">{cluster.label}</span>
        <span className="text-zinc-400">{cluster.members.length}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-100 px-1 py-1">
          {cluster.members.map((a) => (
            <ArtistRow
              key={a.profile.id}
              artist={a}
              onClick={() => onArtistClick?.(a)}
              selected={selectedArtist?.profile.id === a.profile.id}
              showNeighborCount
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CentralityDataPanel({ data, onArtistClick, selectedArtist }: CentralityDataPanelProps) {
  const { clusters, connectors, isolated, stats } = useMemo(() => {
    const points = data.map((d) => ({ lat: d.latitude, lng: d.longitude }));
    const result = dbscan(points, 50, 3);

    const clusterList: Cluster[] = result.clusters.map((indices) => {
      const members = indices.map((i) => data[i]);
      const cityCount = new Map<string, number>();
      for (const m of members) {
        const loc = m.profile.city || m.profile.region || m.profile.country || 'Unknown';
        cityCount.set(loc, (cityCount.get(loc) ?? 0) + 1);
      }
      let bestLabel = 'Unknown';
      let bestCount = 0;
      for (const [label, count] of cityCount) {
        if (count > bestCount) {
          bestLabel = label;
          bestCount = count;
        }
      }
      return { label: bestLabel, members };
    });

    clusterList.sort((a, b) => b.members.length - a.members.length);

    const conn = data.filter((d) => d.neighborCount >= 3).sort((a, b) => b.neighborCount - a.neighborCount);

    const iso = data
      .filter((d) => d.neighborCount === 0)
      .sort((a, b) => (a.profile.displayName ?? '').localeCompare(b.profile.displayName ?? ''));

    const totalNeighbors = data.reduce((s, d) => s + d.neighborCount, 0);

    return {
      clusters: clusterList,
      connectors: conn,
      isolated: iso,
      stats: {
        totalArtists: data.length,
        avgNeighbors: data.length ? (totalNeighbors / data.length).toFixed(1) : '0',
        marketCount: clusterList.length,
        isolatedCount: iso.length,
      },
    };
  }, [data]);

  return (
    <div className="flex h-full w-96 flex-col border-l border-zinc-200 bg-white">
      <div className="grid grid-cols-4 gap-px border-b border-zinc-200 bg-zinc-100">
        {[
          { label: 'Artists', value: stats.totalArtists },
          { label: 'Avg Neighbors', value: stats.avgNeighbors },
          { label: 'Markets', value: stats.marketCount },
          { label: 'Isolated', value: stats.isolatedCount },
        ].map((s) => (
          <div key={s.label} className="bg-white px-3 py-2 text-center">
            <p className="text-sm font-semibold text-zinc-900">{s.value}</p>
            <p className="text-[10px] text-zinc-500">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="markets" className="flex flex-1 flex-col overflow-hidden">
        <TabsList className="mx-3 mt-3 shrink-0">
          <TabsTrigger value="markets" className="gap-1 text-xs">
            <MapPin className="h-3 w-3" /> Markets
          </TabsTrigger>
          <TabsTrigger value="connectors" className="gap-1 text-xs">
            <Users className="h-3 w-3" /> Connectors
          </TabsTrigger>
          <TabsTrigger value="isolated" className="gap-1 text-xs">
            <UserX className="h-3 w-3" /> Isolated
          </TabsTrigger>
        </TabsList>

        <TabsContent value="markets" className="flex-1 overflow-y-auto px-3 pb-3">
          <div className="space-y-2">
            {clusters.length === 0 && <p className="py-4 text-center text-xs text-zinc-400">No clusters detected</p>}
            {clusters.map((c, i) => (
              <ClusterCard key={i} cluster={c} onArtistClick={onArtistClick} selectedArtist={selectedArtist} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="connectors" className="flex-1 overflow-y-auto px-3 pb-3">
          <div className="space-y-0.5">
            {connectors.length === 0 && <p className="py-4 text-center text-xs text-zinc-400">No connectors found</p>}
            {connectors.map((a) => (
              <ArtistRow
                key={a.profile.id}
                artist={a}
                onClick={() => onArtistClick?.(a)}
                selected={selectedArtist?.profile.id === a.profile.id}
                showNeighborCount
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="isolated" className="flex-1 overflow-y-auto px-3 pb-3">
          <div className="space-y-0.5">
            {isolated.length === 0 && <p className="py-4 text-center text-xs text-zinc-400">No isolated artists</p>}
            {isolated.map((a) => (
              <ArtistRow
                key={a.profile.id}
                artist={a}
                onClick={() => onArtistClick?.(a)}
                selected={selectedArtist?.profile.id === a.profile.id}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default CentralityDataPanel;
